import React, { useState, useEffect, useRef, useCallback } from "react";
import { format, parseISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { getCallLogs, getRecording } from "../../services/ndrLiveCallService";

const LIVE_STATUSES = new Set(["INITIATED", "RINGING", "ANSWERED"]);
const RECORDING_POLL_WINDOW_MS = 2 * 60 * 1000;

const CALL_STATUS_DISPLAY = {
  INITIATED: { label: "Calling…", color: "bg-blue-100 text-blue-700", pulse: true },
  RINGING: { label: "Ringing…", color: "bg-blue-100 text-blue-700", pulse: true },
  ANSWERED: { label: "In progress", color: "bg-indigo-100 text-indigo-700", pulse: true },
  COMPLETED: { label: "Completed", color: "bg-green-100 text-green-700" },
  NO_ANSWER: { label: "No answer", color: "bg-gray-100 text-gray-600" },
  FAILED: { label: "Failed", color: "bg-red-100 text-red-700" },
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const istDate = toZonedTime(parseISO(dateString), "Asia/Kolkata");
    return format(istDate, "dd MMM yyyy, HH:mm");
  } catch {
    return dateString;
  }
};

export default function NDRLiveCallsPanel({ ndr }) {
  const merchantId = localStorage.getItem("merchantId") || ndr?.merchant_id;
  const awbNumber = ndr?.awb_number;
  const orderReferenceNumber = ndr?.order_reference_number;

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [recCache, setRecCache] = useState({});
  const [playingId, setPlayingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [recError, setRecError] = useState(null);

  const audioRef = useRef(null);
  const recordingDeadlineRef = useRef(0);

  const shouldContinuePolling = useCallback((rows) => {
    const anyLive = rows.some((r) => LIVE_STATUSES.has(r.call_status));
    if (anyLive) {
      recordingDeadlineRef.current = 0;
      return true;
    }
    const anyPending = rows.some((r) => r.recording_status === "PENDING");
    if (anyPending) {
      if (!recordingDeadlineRef.current) {
        recordingDeadlineRef.current = Date.now() + RECORDING_POLL_WINDOW_MS;
      }
      return Date.now() < recordingDeadlineRef.current;
    }
    recordingDeadlineRef.current = 0;
    return false;
  }, []);

  const fetchLogs = useCallback(
    async ({ silent = false } = {}) => {
      if (!awbNumber && !orderReferenceNumber) {
        setLogs([]);
        return [];
      }
      try {
        if (!silent) setLoading(true);
        setError(null);
        const rows = await getCallLogs({ merchantId, awbNumber, orderReferenceNumber });
        setLogs(rows);
        return rows;
      } catch (err) {
        console.error("Error fetching live call logs:", err);
        setError(
          err.response?.data?.context_data?.reason ||
            err.response?.data?.message ||
            "Failed to load call history"
        );
        return null;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [merchantId, awbNumber, orderReferenceNumber]
  );

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const schedule = (rows) => {
      if (cancelled || !rows) return;
      if (shouldContinuePolling(rows)) {
        timer = setTimeout(async () => {
          const next = await fetchLogs({ silent: true });
          schedule(next);
        }, 5000);
      }
    };

    (async () => {
      const rows = await fetchLogs();
      schedule(rows);
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [fetchLogs, shouldContinuePolling]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  const recordingNotReadyMessage = (err) => {
    const status = err?.response?.status;
    if (status === 404) return "Recording is not ready yet. Please try again in a few seconds.";
    return (
      err?.response?.data?.context_data?.reason ||
      err?.response?.data?.message ||
      "Could not load the recording. Please try again."
    );
  };

  const ensureRecording = async (callLogId) => {
    if (recCache[callLogId]) return recCache[callLogId];
    const rec = await getRecording(callLogId);
    setRecCache((prev) => ({ ...prev, [callLogId]: rec }));
    return rec;
  };

  const handlePlay = async (log) => {
    const id = log.call_log_id;
    setRecError(null);

    if (playingId === id && audioRef.current) {
      if (audioRef.current.paused) audioRef.current.play();
      else audioRef.current.pause();
      return;
    }

    try {
      setBusyId(id);
      await ensureRecording(id);
      setPlayingId(id);
    } catch (err) {
      setRecError(recordingNotReadyMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    if (!playingId) return;
    const rec = recCache[playingId];
    if (rec?.stream_url && audioRef.current) {
      audioRef.current.src = rec.stream_url;
      audioRef.current.play().catch(() => {});
    }
  }, [playingId, recCache]);

  const handleDownload = async (log) => {
    const id = log.call_log_id;
    setRecError(null);
    try {
      setBusyId(id);
      const rec = await ensureRecording(id);
      if (rec?.download_url) {
        const link = document.createElement("a");
        link.href = rec.download_url;
        link.download = `${log.awb_number || "recording"}-live-call.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      setRecError(recordingNotReadyMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const activeLog = playingId ? logs.find((l) => l.call_log_id === playingId) : null;

  const renderOutcome = (log) => {
    const status = CALL_STATUS_DISPLAY[log.call_status] || {
      label: log.call_status || "-",
      color: "bg-gray-100 text-gray-600",
    };
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 ${status.color}`}
        >
          {status.pulse && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
          {status.label}
        </span>
        {log.connected ? (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Connected
          </span>
        ) : (
          !LIVE_STATUSES.has(log.call_status) && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              Not connected
            </span>
          )
        )}
      </div>
    );
  };

  const renderRecordingControls = (log) => {
    const id = log.call_log_id;
    const status = log.recording_status;
    const isBusy = busyId === id;

    if (status === "AVAILABLE") {
      const isPlaying = playingId === id;
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePlay(log)}
            disabled={isBusy}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 text-xs font-medium disabled:opacity-50"
          >
            {isBusy ? (
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {isPlaying ? "Playing" : "Play"}
          </button>
          <button
            onClick={() => handleDownload(log)}
            disabled={isBusy}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1.5 text-xs font-medium disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download
          </button>
        </div>
      );
    }

    if (status === "PENDING") {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 font-medium">
          <span className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          Processing recording…
        </span>
      );
    }

    if (status === "FAILED") {
      return <span className="text-xs text-gray-400">Recording unavailable</span>;
    }

    return <span className="text-xs text-gray-400">No recording</span>;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="text-sm text-gray-600">
          {loading ? "Loading call history…" : `${logs.length} call${logs.length === 1 ? "" : "s"} placed`}
          {logs.some((l) => LIVE_STATUSES.has(l.call_status)) && (
            <span className="ml-2 inline-flex items-center gap-1 text-blue-600 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
              Live
            </span>
          )}
        </div>
        <button
          onClick={() => fetchLogs()}
          disabled={loading}
          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-3 rounded text-sm text-red-700">{error}</div>
      )}
      {recError && (
        <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 p-3 rounded text-sm text-amber-700">
          {recError}
        </div>
      )}

      {loading && logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="mt-4 text-gray-600 text-sm">Loading call history…</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="w-14 h-14 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <p className="text-gray-600">No calls have been placed for this shipment yet.</p>
          <p className="text-gray-400 text-sm mt-1">Call history and recordings will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <div
              key={log.call_log_id}
              className="border border-gray-200 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {renderOutcome(log)}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5 mt-3 text-xs text-gray-600">
                    <div>
                      <span className="text-gray-400">When</span>
                      <div className="font-medium text-gray-700">{formatDate(log.initiated_date_time)}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Talked</span>
                      <div className="font-medium text-gray-700">{log.talk_time_display || "—"}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Attempt</span>
                      <div className="font-medium text-gray-700">{log.delivery_attempt_number ?? "—"}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Agent</span>
                      <div className="font-medium text-gray-700 truncate">{log.agent_user_id || "—"}</div>
                    </div>
                  </div>
                </div>
                <div className="shrink-0 flex sm:flex-col sm:items-end gap-2">{renderRecordingControls(log)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeLog && recCache[activeLog.call_log_id] && (
        <div className="sticky bottom-0 mt-4 bg-gray-50 border border-gray-200 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2 text-xs font-medium text-gray-600">
            <span className="w-2 h-2 rounded-full bg-blue-600" />
            Now playing — {formatDate(activeLog.initiated_date_time)}
            {activeLog.talk_time_display ? ` · ${activeLog.talk_time_display}` : ""}
          </div>
          <audio ref={audioRef} controls className="w-full" onEnded={() => setPlayingId(null)}>
            Your browser does not support the audio element.
          </audio>
        </div>
      )}
    </div>
  );
}
